/**
 * Reading Progress — Lesefortschritts-Progressbar
 *
 * Markup (ausgegeben von fe_page.html.twig wenn addReadingProgress gesetzt):
 *   <div id="reading-progress-bar" role="progressbar"
 *        aria-label="Lesefortschritt"
 *        aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
 *     <div id="reading-progress-fill"></div>
 *   </div>
 *
 * Fortschritts-Berechnung:
 *   Primär: scrollY / (scrollHeight - innerHeight) — universell, kein Selektor nötig.
 *   Optional: Wenn [data-reading-content] im DOM ist, wird nur der Bereich vom
 *   Artikelanfang (offsetTop) bis Artikelende (offsetTop + offsetHeight) als
 *   100%-Spanne verwendet. Das liefert auf News-Detailseiten präziseres Feedback
 *   ("Artikel fertig gelesen") statt "Seite bis Footer gescrollt".
 *
 * Performance:
 *   - Scroll + Resize über rAF throttled (passiv)
 *   - aria-valuenow wird nur bei ≥1%-Änderung aktualisiert (kein Flut-Fire)
 *   - prefers-reduced-motion: Bar bleibt sichtbar, aber keine CSS-Transition
 *
 * No-Op-Garantie: init() prüft sofort ob #reading-progress-bar im DOM ist.
 */

const BAR_ID = "reading-progress-bar";
const FILL_ID = "reading-progress-fill";

/**
 * Berechnet den Fortschritt (0–1) relativ zum Artikel-Content-Element oder
 * zum gesamten Dokument als Fallback.
 *
 * @param {HTMLElement|null} contentEl
 * @returns {number} 0–1
 */
function calcProgress(contentEl) {
  const scrollY = window.scrollY;

  if (contentEl) {
    const rect = contentEl.getBoundingClientRect();
    const contentTop = scrollY + rect.top; // Artikel-Oberkante im Dokument
    const contentHeight = contentEl.offsetHeight;
    // Scrollbare Strecke durch den Artikel: von "Oberkante am Viewport-Top"
    // bis "Unterkante am Viewport-Bottom". So ist der Fortschritt 0 %, solange
    // noch nicht gescrollt wurde (oben), und 100 % erst am Artikel-Ende.
    const range = contentHeight - window.innerHeight;
    if (range <= 0) return 0; // Artikel kürzer als Viewport → nichts zu tracken
    const progress = (scrollY - contentTop) / range;
    return Math.min(1, Math.max(0, progress));
  }

  // Fallback: Dokument-Scroll
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  if (maxScroll <= 0) return 1;
  return Math.min(1, scrollY / maxScroll);
}

export function init() {
  const bar = document.getElementById(BAR_ID);
  if (!bar) return; // No-Op: Bar nicht im DOM → Seite hat addReadingProgress nicht gesetzt

  const fill = document.getElementById(FILL_ID);
  if (!fill) return;

  // Artikel-Content-Element: zuerst explizites data-Attribut, dann Fallback
  // auf article.layout_full (Contao News-Vollansicht).
  // Wenn weder noch vorhanden (z.B. Newsübersicht), Bar ausblenden — sie
  // macht auf Listenseiten ohne klar definierten Lesebereich keinen Sinn.
  const contentEl =
    document.querySelector("[data-reading-content]") ??
    document.querySelector("article.layout_full") ??
    null;

  if (!contentEl) {
    bar.hidden = true;
    return;
  }

  // Bar direkt UNTER dem Sticky-Header andocken statt bei top:0 (wo der Header
  // mit höherem z-index sie verdecken würde). Header ist variabel hoch
  // (mobile vs. lg), daher gemessen statt fest verdrahtet; bei Resize aktualisiert.
  const stickyHeader = document.querySelector("[data-sticky-header]");
  function positionBelowHeader() {
    bar.style.top = (stickyHeader ? stickyHeader.offsetHeight : 0) + "px";
  }
  positionBelowHeader();
  window.addEventListener("resize", positionBelowHeader, { passive: true });

  // reduced-motion: Keine Transition auf dem Fill-Element
  const prefersReduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReduced) {
    fill.style.transitionDuration = "0s";
  }

  let rafId = null;
  let lastAriaValue = 0;

  function update() {
    rafId = null;
    const progress = calcProgress(contentEl);
    const pct = Math.round(progress * 100);

    // Fill-Breite setzen (CSS-Transition animiert, außer bei reduced-motion)
    fill.style.width = pct + "%";

    // aria-valuenow nur bei ≥1-Punkt-Änderung aktualisieren — verhindert
    // Screen-Reader-Überflutung bei jedem Pixel
    if (Math.abs(pct - lastAriaValue) >= 1) {
      bar.setAttribute("aria-valuenow", pct);
      lastAriaValue = pct;
    }
  }

  function onScroll() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(update);
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });

  // Initialer Zustand (z.B. nach Back-Navigation mit gespeicherter Scroll-Position)
  update();
}
